import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/**
 * Admin → Clinical → Intake Form → the Title dropdown shown in front of the
 * patient's name at Registration ([Mr. ▼][Ramesh Kumar]).
 *
 * Controlled: the parent's one "Save configuration" button sends the status
 * and this list together (PUT /api/intake-config/bulk), and the server
 * re-validates with backend/utils/patientName.js#validateTitleOptions —
 * the checks here only stop an obviously bad list before the round trip.
 */

export type FieldStatus = 'mandatory' | 'optional' | 'hidden';

export const MAX_TITLE_LENGTH = 20;
export const MAX_TITLE_OPTIONS = 12;

const STATUS_LABEL: Record<FieldStatus, string> = { mandatory: 'Mandatory', optional: 'Optional', hidden: 'Hidden' };
const STATUS_CLASS: Record<FieldStatus, string> = {
  mandatory: 'border-success bg-success-soft text-success-ink',
  optional: 'border-info bg-info-soft text-info-ink',
  hidden: 'border-border bg-muted text-ink-muted',
};

const key = (t: string) => t.trim().replace(/\.+$/, '').toLowerCase();

/** First problem with the list, or null. Mirrors the server's rules. */
export function titleOptionsProblem(options: string[]): string | null {
  if (!options.length) return 'Add at least one title, or set Title to Hidden instead.';
  if (options.length > MAX_TITLE_OPTIONS) return `At most ${MAX_TITLE_OPTIONS} titles.`;
  const seen = new Set<string>();
  for (const o of options) {
    const t = o.trim();
    if (!t) return 'A title cannot be blank.';
    if (t.length > MAX_TITLE_LENGTH) return `"${t}" is too long — keep each title to ${MAX_TITLE_LENGTH} characters.`;
    if (seen.has(key(t))) return `"${t}" is listed twice.`;
    seen.add(key(t));
  }
  return null;
}

interface Props {
  status: FieldStatus;
  options: string[];
  onStatusChange: (status: FieldStatus) => void;
  onOptionsChange: (options: string[]) => void;
}

export function TitleOptionsCard({ status, options, onStatusChange, onOptionsChange }: Props) {
  const [draft, setDraft] = useState('');
  const [addError, setAddError] = useState('');
  const problem = titleOptionsProblem(options);

  const add = () => {
    const t = draft.trim().replace(/\s+/g, ' ');
    if (!t) return;
    if (t.length > MAX_TITLE_LENGTH) { setAddError(`Keep it to ${MAX_TITLE_LENGTH} characters.`); return; }
    if (options.some((o) => key(o) === key(t))) { setAddError(`"${t}" is already in the list.`); return; }
    if (options.length >= MAX_TITLE_OPTIONS) { setAddError(`At most ${MAX_TITLE_OPTIONS} titles.`); return; }
    onOptionsChange([...options, t]);
    setDraft('');
    setAddError('');
  };

  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    onOptionsChange(next);
  };

  const rename = (idx: number, value: string) => onOptionsChange(options.map((o, i) => (i === idx ? value : o)));
  const remove = (idx: number) => onOptionsChange(options.filter((_, i) => i !== idx));

  return (
    <div className="mb-5 rounded-[var(--radius)] border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold">Title (before patient name)</div>
          <div className="mt-0.5 text-[12px] text-ink-muted">
            Shown in front of the name box at Registration, e.g. <span className="font-mono">[Mr. ▼] [Ramesh Kumar]</span>.
            Staff choose it — it is never filled in from gender.
          </div>
        </div>
        <div className="flex gap-1.5">
          {(Object.keys(STATUS_LABEL) as FieldStatus[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onStatusChange(s)}
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
                status === s ? STATUS_CLASS[s] : 'border-transparent bg-muted text-ink-muted'
              }`}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      <div className={status === 'hidden' ? 'mt-3 opacity-60' : 'mt-3'}>
        <div className="mb-1.5 text-[12px] font-semibold text-ink-muted">Dropdown options</div>
        <div className="flex flex-col gap-1.5">
          {options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="flex flex-col">
                <button type="button" aria-label={`Move ${opt} up`} disabled={idx === 0}
                  onClick={() => move(idx, -1)} className="px-1.5 text-[10px] text-ink-muted disabled:opacity-30">▲</button>
                <button type="button" aria-label={`Move ${opt} down`} disabled={idx === options.length - 1}
                  onClick={() => move(idx, 1)} className="px-1.5 text-[10px] text-ink-muted disabled:opacity-30">▼</button>
              </div>
              <Input
                aria-label={`Title option ${idx + 1}`}
                className="max-w-[200px]"
                value={opt}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(e) => rename(idx, e.target.value)}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => remove(idx)} aria-label={`Remove ${opt}`}>
                ✕ Remove
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-2.5 flex items-center gap-2">
          <Input
            aria-label="New title"
            className="max-w-[200px]"
            placeholder="e.g. Dr., Master, Baby"
            value={draft}
            maxLength={MAX_TITLE_LENGTH}
            onChange={(e) => { setDraft(e.target.value); setAddError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          />
          <Button type="button" variant="ghost" size="sm" onClick={add} disabled={!draft.trim()}>
            + Add title
          </Button>
        </div>
        {(addError || problem) && <div className="mt-1.5 text-[12px] text-danger">{addError || problem}</div>}
        <div className="mt-2 text-[11.5px] text-ink-muted">
          Removing or renaming a title here does not change patients already registered with it.
        </div>
      </div>
    </div>
  );
}
